package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.common.Formatters
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsConfig
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsSavedController
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.GemType
import diana.soleil.design.iraniancanadienbazzar.core.model.Joke
import diana.soleil.design.iraniancanadienbazzar.core.model.JokeCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.JokeMaturity
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** Report lifecycle for a gem (mirrors the feed's pattern, with a sign-in gate for guests). */
enum class GemReportStatus { Idle, Sending, Sent, SignInRequired, Failed }

/**
 * Jokes browser state. [jokes] is the filtered/searched pager list; [totalCount] is the size of
 * the corpus under the current tier (for the "12/124" counter).
 */
data class JokesUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val jokes: List<Joke> = emptyList(),
    val totalCount: Int = 0,
    /** Selected category chip; null = All. */
    val category: JokeCategory? = null,
    /** True while the adult tier chip is selected (only possible after the age gate). */
    val adultTier: Boolean = false,
    /** Remote Config `gems_adult_jokes_enabled` — when false the adult chip does not exist. */
    val adultTierAvailable: Boolean = false,
    /** The 18+ self-declaration (local pref OR `users/{uid}.adultContentOptIn`). */
    val adultOptIn: Boolean = false,
    /** The age-gate sheet is showing (adult chip tapped without a stored opt-in). */
    val showAgeGate: Boolean = false,
    val query: String = "",
    val likedJokeIds: Set<String> = emptySet(),
    val savedJokeIds: Set<String> = emptySet(),
    /** Guest tried to like — the route sends them to sign-in. */
    val signInRequired: Boolean = false,
    val reportStatus: GemReportStatus = GemReportStatus.Idle,
)

/**
 * Full-screen jokes pager (Stitch `02356d62`): streams the published corpus and applies category +
 * trilingual client-side search ([Formatters.normalizePersian] haystack — SearchViewModel pattern;
 * the corpus is small by design). The adult tier is triple-gated: Remote Config
 * `gems_adult_jokes_enabled` (chip hidden when off), the 18+ age-gate opt-in, and rules that deny
 * adult reads to signed-out sessions (`docs/HIDDEN_GEMS_PLAN.md` §3).
 */
@OptIn(ExperimentalCoroutinesApi::class)
class JokesViewModel(
    private val gemsRepository: GemsRepository,
    private val savedController: GemsSavedController,
    gemsConfig: GemsConfig,
    private val reportRepository: ReportRepository,
    private val preferences: AppPreferences,
) : ViewModel() {

    private val categoryFlow = MutableStateFlow<JokeCategory?>(null)
    private val adultTierFlow = MutableStateFlow(false)
    private val queryFlow = MutableStateFlow("")
    private val localOptIn = MutableStateFlow(preferences.gemsAdultOptIn)
    private val showAgeGate = MutableStateFlow(false)
    private val signInGate = MutableStateFlow(false)
    private val reportStatus = MutableStateFlow(GemReportStatus.Idle)

    /** Effective 18+ opt-in: device-local pref OR the account flag (union survives sign-in). */
    private val optedIn = combine(localOptIn, gemsRepository.observeAdultOptIn()) { local, cloud ->
        local || cloud == true
    }

    // Adult tier is only queryable when the RC flag is on AND the user opted in AND is signed in
    // (rules deny adult docs to anonymous sessions — the query must stay provably clean for them).
    private val includeAdult = combine(gemsConfig.adultJokesEnabled, optedIn) { enabled, opted ->
        enabled && opted && gemsRepository.currentUid != null
    }

    private val corpus = includeAdult.flatMapLatest { adult ->
        gemsRepository.observeJokes(includeAdult = adult)
    }

    val state: StateFlow<JokesUiState> =
        combine(
            combine(corpus, gemsRepository.observeLikedJokeIds(), savedController.saved) { c, liked, saved ->
                Triple(c, liked, saved)
            },
            combine(categoryFlow, adultTierFlow, queryFlow) { cat, adult, query -> Triple(cat, adult, query) },
            combine(gemsConfig.adultJokesEnabled, optedIn) { available, opted -> available to opted },
            combine(showAgeGate, signInGate, reportStatus) { gate, signIn, report -> Triple(gate, signIn, report) },
        ) { (result, liked, saved), (category, adultTier, query), (adultAvailable, adultOptIn), (ageGate, signIn, report) ->
            when (result) {
                is DataResult.Success -> {
                    val tiered = result.data.filter { joke ->
                        if (adultTier) joke.maturity == JokeMaturity.Adult else joke.maturity == JokeMaturity.Clean
                    }
                    JokesUiState(
                        loading = false,
                        jokes = tiered.applyJokeFilters(category, query),
                        totalCount = tiered.size,
                        category = category,
                        adultTier = adultTier,
                        adultTierAvailable = adultAvailable,
                        adultOptIn = adultOptIn,
                        showAgeGate = ageGate,
                        query = query,
                        likedJokeIds = liked,
                        savedJokeIds = saved.filter { it.type == GemType.Joke }.map { it.id }.toSet(),
                        signInRequired = signIn,
                        reportStatus = report,
                    )
                }
                is DataResult.Failure -> JokesUiState(
                    loading = false,
                    error = true,
                    adultTierAvailable = adultAvailable,
                    showAgeGate = ageGate,
                    signInRequired = signIn,
                    reportStatus = report,
                )
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), JokesUiState())

    fun setCategory(category: JokeCategory?) {
        categoryFlow.value = category
    }

    fun onQueryChange(query: String) {
        queryFlow.value = query
    }

    /** Adult chip tapped: pass straight through when already opted in, else raise the age gate. */
    fun onAdultTierRequested() {
        viewModelScope.launch {
            val opted = preferences.gemsAdultOptIn || state.value.adultOptIn
            if (opted) adultTierFlow.value = true else showAgeGate.value = true
        }
    }

    fun onCleanTierSelected() {
        adultTierFlow.value = false
    }

    /** Age-gate confirm: persists locally + on the account (best-effort), then opens the tier. */
    fun confirmAdultOptIn() {
        preferences.gemsAdultOptIn = true
        localOptIn.value = true
        showAgeGate.value = false
        adultTierFlow.value = true
        viewModelScope.launch { gemsRepository.setAdultOptIn(true) } // guests: local pref only
    }

    fun dismissAgeGate() {
        showAgeGate.value = false
    }

    /** Toggles the caller's like; guests bounce to sign-in via the gate (double-tap or rail). */
    fun toggleLike(jokeId: String, liked: Boolean) {
        viewModelScope.launch {
            val result = gemsRepository.setJokeLiked(jokeId, !liked)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) {
                signInGate.value = true
            }
        }
    }

    /** Toggles the private bookmark — works for guests too (device-local, cloud when signed in). */
    fun toggleSave(jokeId: String) {
        viewModelScope.launch { savedController.toggle(SavedGem(id = jokeId, type = GemType.Joke)) }
    }

    fun consumeSignInGate() {
        signInGate.value = false
    }

    fun report(jokeId: String, reason: String) {
        if (reportStatus.value == GemReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = GemReportStatus.Sending
            reportStatus.value = when (val result = reportRepository.fileReport("joke", jokeId, reason)) {
                is DataResult.Success -> GemReportStatus.Sent
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) GemReportStatus.SignInRequired
                    else GemReportStatus.Failed
            }
        }
    }

    fun resetReport() {
        reportStatus.value = GemReportStatus.Idle
    }
}

private fun List<Joke>.applyJokeFilters(category: JokeCategory?, query: String): List<Joke> {
    val normalized = Formatters.normalizePersian(query).trim().lowercase()
    val tokens = normalized.split(' ').filter { it.isNotBlank() }
    return filter { joke ->
        (category == null || joke.category == category) &&
            (tokens.isEmpty() || tokens.all { token -> joke.searchHaystack().contains(token) })
    }
}

/** Lowercased, Persian-normalized bag of every text variant + tags (SearchViewModel pattern). */
private fun Joke.searchHaystack(): String =
    Formatters.normalizePersian(
        buildList {
            addAll(listOfNotNull(text.en, text.fr, text.fa))
            addAll(tags)
        }.joinToString(" "),
    ).lowercase()
